import { useState, useEffect, useCallback, useRef } from 'react';
import { useCurrentUser } from '@/stores/authStore';

interface DocumentStatusUpdate {
  document_id: string;
  status: string;
  progress: number;
  company_id: number;
  timestamp: string;
}

interface UseDocumentWebSocketOptions {
  companyId?: number;
  documentIds?: string[];  // 初始订阅文档ID列表
  onStatusUpdate?: (update: DocumentStatusUpdate) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  autoConnect?: boolean;  // 是否自动连接
  reconnectInterval?: number;  // 重连间隔(ms)
}

interface UseDocumentWebSocketReturn {
  // 状态
  documentStatuses: Map<string, DocumentStatusUpdate>;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;

  // 操作方法
  connect: () => void;
  disconnect: () => void;
  subscribeToDocuments: (documentIds: string[]) => void;
  unsubscribeFromDocuments: (documentIds: string[]) => void;
  getDocumentStatus: (documentId: string) => DocumentStatusUpdate | undefined;

  // 统计信息
  stats: {
    totalUpdates: number;
    lastUpdateTime: string | null;
    subscribedDocuments: number;
  };
}

/**
 * WebSocket文档状态管理钩子
 * 用于实时接收文档处理状态更新
 */
export const useDocumentWebSocket = (options: UseDocumentWebSocketOptions = {}): UseDocumentWebSocketReturn => {
  const {
    companyId,
    documentIds = [],
    onStatusUpdate,
    onConnected,
    onDisconnected,
    autoConnect = true,
    reconnectInterval = 5000,
  } = options;

  const user = useCurrentUser();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef(false); // 防止重复连接的锁
  const connectFnRef = useRef<(() => void) | null>(null); // 存储 connect 函数引用
  const disconnectFnRef = useRef<(() => void) | null>(null); // 存储 disconnect 函数引用
  const isMountedRef = useRef(true); // 组件挂载状态

  // 状态管理
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentStatuses, setDocumentStatuses] = useState<Map<string, DocumentStatusUpdate>>(new Map());
  const [stats, setStats] = useState({
    totalUpdates: 0,
    lastUpdateTime: null as string | null,
    subscribedDocuments: 0,
  });

  // 去重缓存：避免重复处理相同状态
  const processedMessages = useRef<Set<string>>(new Set());

  // 构建WebSocket URL（使用相对路径，由 Vite proxy 转发到后端）
  const buildWebSocketUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    // 生成符合后端验证格式的session_id：必须以'sess_'或'session_'开头，长度20-100，只包含字母数字和-_.
    const sessionId = `sess_doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return `${protocol}//${host}/ws/chat/${sessionId}`;
  }, []);

  // 生成消息ID用于去重
  const generateMessageId = (update: DocumentStatusUpdate): string => {
    return `${update.document_id}_${update.status}_${update.timestamp}`;
  };

  // 处理WebSocket消息
  const handleWebSocketMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      const messageType = data.type;

      if (messageType === 'document_status_update') {
        const update: DocumentStatusUpdate = data.data;

        // 消息去重
        const messageId = generateMessageId(update);
        if (processedMessages.current.has(messageId)) {
          return;
        }
        processedMessages.current.add(messageId);
        // 清理旧消息（保留最近1000条）
        if (processedMessages.current.size > 1000) {
          const first = processedMessages.current.keys().next().value;
          if (first) processedMessages.current.delete(first);
        }

        // 更新状态
        setDocumentStatuses(prev => {
          const newMap = new Map(prev);
          newMap.set(update.document_id, update);
          return newMap;
        });

        // 更新统计
        setStats(prev => ({
          ...prev,
          totalUpdates: prev.totalUpdates + 1,
          lastUpdateTime: new Date().toISOString(),
        }));

        // 调用回调
        if (onStatusUpdate) {
          onStatusUpdate(update);
        }

        console.log('文档状态更新:', update);
      } else if (messageType === 'handshake_ack') {
        console.log('WebSocket握手成功:', data);
      } else if (messageType === 'subscribe_ack') {
        console.log('文档订阅确认:', data);
      } else if (messageType === 'unsubscribe_ack') {
        console.log('文档取消订阅确认:', data);
      } else if (messageType === 'error') {
        console.error('WebSocket错误:', data.message);
        setError(data.message);
      } else if (messageType === 'heartbeat') {
        // 心跳响应，忽略
      }
    } catch (err) {
      console.error('处理WebSocket消息失败:', err);
    }
  }, [onStatusUpdate]);

  // 发送WebSocket消息
  const sendWebSocketMessage = useCallback((message: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    console.warn('WebSocket未连接，无法发送消息');
    return false;
  }, []);

  // 发送握手消息
  const sendHandshake = useCallback(() => {
    if (!companyId || companyId <= 0) {
      console.warn('未提供有效的companyId，跳过握手');
      return;
    }

    const handshakeMessage = {
      type: 'handshake',
      conversation_id: `documents_${companyId}`,
      company_id: companyId,
      timestamp: new Date().toISOString(),
    };

    sendWebSocketMessage(handshakeMessage);
  }, [companyId, sendWebSocketMessage]);

  // 发送订阅消息
  const sendSubscribe = useCallback((docIds: string[]) => {
    if (!companyId || companyId <= 0 || docIds.length === 0) {
      return;
    }

    const subscribeMessage = {
      type: 'subscribe_document_updates',
      company_id: companyId,
      document_ids: docIds,
      timestamp: new Date().toISOString(),
    };

    sendWebSocketMessage(subscribeMessage);
  }, [companyId, sendWebSocketMessage]);

  // 发送取消订阅消息
  const sendUnsubscribe = useCallback((docIds: string[]) => {
    const unsubscribeMessage = {
      type: 'unsubscribe_document_updates',
      document_ids: docIds,
      timestamp: new Date().toISOString(),
    };

    sendWebSocketMessage(unsubscribeMessage);
  }, [sendWebSocketMessage]);

  // 发送心跳
  const sendHeartbeat = useCallback(() => {
    const heartbeatMessage = {
      type: 'heartbeat',
      timestamp: new Date().toISOString(),
    };

    sendWebSocketMessage(heartbeatMessage);
  }, [sendWebSocketMessage]);

  // 断开连接 - 使用函数而非 useCallback 避免依赖循环
  const disconnect = () => {
    // 释放连接锁
    isConnectingRef.current = false;

    // 清理重连定时器
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // 清理心跳定时器
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }

    // 关闭WebSocket连接（包括 CONNECTING 状态，避免 StrictMode 孤儿连接）
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close(1000, '用户主动断开');
      }
      wsRef.current = null;
    }

    setIsConnected(false);
    setIsConnecting(false);
    console.log('WebSocket连接已断开');
  };
  
  // 存储到 ref 中供 useEffect 使用
  disconnectFnRef.current = disconnect;

  // 建立WebSocket连接 - 使用函数而非 useCallback 避免依赖循环
  const connect = () => {
    // 检查连接锁，防止重复连接
    if (isConnectingRef.current) {
      console.log('WebSocket连接中（锁保护），跳过重复连接');
      return;
    }

    // 检查companyId是否有效 - 必须大于0
    if (!companyId || companyId <= 0) {
      console.warn('companyId无效，跳过WebSocket连接');
      setError('companyId无效，无法连接WebSocket');
      setIsConnecting(false);
      return;
    }

    // 检查是否已连接
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('WebSocket已连接，无需重新连接');
      return;
    }

    // 如果存在旧连接且处于CLOSING/CONNECTING状态，先清理
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.CONNECTING) {
        console.log('WebSocket连接中，等待完成或超时');
        return;
      }
      // CLOSING 或 CLOSED 状态，清理旧连接
      console.log('清理旧WebSocket连接');
      if (disconnectFnRef.current) {
        disconnectFnRef.current();
      }
    }

    // 设置连接锁
    isConnectingRef.current = true;
    setIsConnecting(true);
    setError(null);

    try {
      const wsUrl = buildWebSocketUrl();
      console.log('开始WebSocket连接:', wsUrl);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket连接已建立，状态:', ws.readyState);
        isConnectingRef.current = false; // 释放连接锁
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);

        // 立即发送握手消息，不通过函数调用
        if (companyId && companyId > 0) {
          const handshakeMessage = {
            type: 'handshake',
            conversation_id: `documents_${companyId}`,
            company_id: companyId,
            timestamp: new Date().toISOString(),
          };
          console.log('发送握手消息:', handshakeMessage);
          try {
            ws.send(JSON.stringify(handshakeMessage));
          } catch (err) {
            console.error('发送握手消息失败:', err);
          }
        }

        // 发送初始订阅
        if (documentIds.length > 0) {
          sendSubscribe(documentIds);
        }

        // 启动心跳
        if (heartbeatTimerRef.current) {
          clearInterval(heartbeatTimerRef.current);
        }
        heartbeatTimerRef.current = setInterval(sendHeartbeat, 30000); // 30秒心跳

        if (onConnected) {
          onConnected();
        }
      };

      ws.onmessage = handleWebSocketMessage;

      ws.onclose = (event) => {
        console.log('WebSocket连接关闭:', event.code, event.reason);
        isConnectingRef.current = false; // 释放连接锁
        setIsConnected(false);
        setIsConnecting(false);

        // 清理心跳
        if (heartbeatTimerRef.current) {
          clearInterval(heartbeatTimerRef.current);
          heartbeatTimerRef.current = null;
        }

        if (onDisconnected) {
          onDisconnected();
        }

        // 自动重连 - 只在组件未卸载且 autoConnect 为 true 时重连
        if (autoConnect && isMountedRef.current && !reconnectTimerRef.current) {
          console.log(`计划 ${reconnectInterval}ms 后重连...`);
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            if (isMountedRef.current && connectFnRef.current) {
              console.log('尝试重新连接...');
              connectFnRef.current();
            }
          }, reconnectInterval);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket错误:', error);
        isConnectingRef.current = false; // 释放连接锁
        setError('WebSocket连接错误');
        setIsConnecting(false);
      };

    } catch (err) {
      console.error('创建WebSocket连接失败:', err);
      isConnectingRef.current = false; // 释放连接锁
      setError('创建连接失败');
      setIsConnecting(false);
    }
  };
  
  // 存储到 ref 中供 useEffect 和重连逻辑使用
  connectFnRef.current = connect;

  // 订阅文档
  const subscribeToDocuments = useCallback((docIds: string[]) => {
    if (!isConnected || docIds.length === 0) {
      return;
    }

    // 过滤已订阅的文档
    const newDocIds = docIds.filter(id => !documentStatuses.has(id));
    if (newDocIds.length === 0) {
      return;
    }

    sendSubscribe(newDocIds);

    // 更新统计
    setStats(prev => ({
      ...prev,
      subscribedDocuments: prev.subscribedDocuments + newDocIds.length,
    }));
  }, [isConnected, documentStatuses, sendSubscribe]);

  // 取消订阅文档
  const unsubscribeFromDocuments = useCallback((docIds: string[]) => {
    if (!isConnected || docIds.length === 0) {
      return;
    }

    sendUnsubscribe(docIds);

    // 从本地状态移除
    setDocumentStatuses(prev => {
      const newMap = new Map(prev);
      docIds.forEach(id => newMap.delete(id));
      return newMap;
    });

    // 更新统计
    setStats(prev => ({
      ...prev,
      subscribedDocuments: Math.max(0, prev.subscribedDocuments - docIds.length),
    }));
  }, [isConnected, sendUnsubscribe]);

  // 获取文档状态
  const getDocumentStatus = useCallback((documentId: string): DocumentStatusUpdate | undefined => {
    return documentStatuses.get(documentId);
  }, [documentStatuses]);

  // 初始化连接 - 只依赖 autoConnect 和 companyId
  useEffect(() => {
    isMountedRef.current = true;
    
    if (autoConnect && companyId && companyId > 0) {
      console.log('初始化 WebSocket 连接, companyId:', companyId);
      if (connectFnRef.current) {
        connectFnRef.current();
      }
    }

    return () => {
      console.log('组件卸载，清理 WebSocket 连接');
      isMountedRef.current = false;
      if (disconnectFnRef.current) {
        disconnectFnRef.current();
      }
    };
  }, [autoConnect, companyId]);

  // 当documentIds变化时更新订阅 - 移除 documentStatuses 依赖避免循环
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    // 订阅新文档（documentIds 变化时）
    if (documentIds.length > 0) {
      // 使用 ref 获取当前状态，避免依赖
      setDocumentStatuses(currentStatuses => {
        const currentDocIds = Array.from(currentStatuses.keys());
        
        // 取消订阅不再需要的文档
        const toUnsubscribe = currentDocIds.filter(id => !documentIds.includes(id));
        if (toUnsubscribe.length > 0) {
          sendUnsubscribe(toUnsubscribe);
        }

        // 订阅新文档
        const toSubscribe = documentIds.filter(id => !currentDocIds.includes(id));
        if (toSubscribe.length > 0) {
          sendSubscribe(toSubscribe);
        }

        return currentStatuses;
      });

      // 更新统计
      setStats(prev => ({
        ...prev,
        subscribedDocuments: documentIds.length,
      }));
    }
  }, [isConnected, documentIds, sendSubscribe, sendUnsubscribe]);

  return {
    // 状态
    documentStatuses,
    isConnected,
    isConnecting,
    error,

    // 操作方法
    connect,
    disconnect,
    subscribeToDocuments,
    unsubscribeFromDocuments,
    getDocumentStatus,

    // 统计信息
    stats,
  };
};